import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { FieldMask, MarketingInformation, MarketplaceAppService } from '@galaxy/marketplace-apps/v1';
import {
  AppPrices,
  DeliveryMethod,
  ItemsApiService,
  ItemType,
  ListActivatableItemsFilters,
  ListActivatableItemsRequest,
  ListActivatableItemsResponse,
  ListActivatableItemsSortField,
  ListActivatableItemsSortOptions,
  RequiredItem,
  SortDirection,
} from '@vendasta/marketplace-apps';
import { retryer } from '@vendasta/rx-utils';
import { File, Product } from '@vendasta/store';
import { BehaviorSubject, combineLatest, Observable, of, take, throwError } from 'rxjs';
import { catchError, filter, map, shareReplay, switchMap } from 'rxjs/operators';
import { Currency, MerchantService } from '@galaxy/billing';
import { AppPricingService, LmiCategory } from '@galaxy/marketplace-apps';
import { PagedResponseInterface } from '@vendasta/galaxy/table';
import {
  convertListItemsAndPricingToTableDataRow,
  ProductListPageTableRow,
} from './product-list-page-table-view/product-list-page-table-row';
import { convertListActivatableItemResponse, convertSDKLmiEnumToDisplayStrings } from './utils';
import { TRAINING_RESOURCES_CONFIG_TOKEN, TrainingResourcesConfig } from '../tokens';
import { MarketplacePackagesApiService } from '@vendasta/marketplace-packages';

export interface ListItem {
  itemId: string;
  name: string;
  iconUrl: string;
  headerImageUrl: string;
  learnMoreUrl: string;
  type: ItemType;
  requiredItems: RequiredItem[];
  deliveryMethod: DeliveryMethod;
}

export interface ListItemWithPageData {
  items: ListItem[];
  nextCursor: string;
  hasMore: boolean;
  totalResults: number;
}

@Injectable()
export class ProductPageService {
  error$: Observable<Error>;

  private readonly error$$: BehaviorSubject<Error> = new BehaviorSubject<Error>(null);
  private readonly errorStrategy$ = retryer<ListActivatableItemsResponse>({
    maxAttempts: 3,
    retryDelay: 200,
    timeoutMilliseconds: 10000,
    customRetryChecker: (errResp: HttpErrorResponse) => errResp.status === 503 || errResp.status === 504,
  });

  private partnerCurrency$: Observable<Currency>;

  private partnerId$: Observable<string> = this.trainingResourcesConfig.partnerId$;
  private marketId$: Observable<string> = this.trainingResourcesConfig.marketId$;

  constructor(
    @Inject(TRAINING_RESOURCES_CONFIG_TOKEN) private readonly trainingResourcesConfig: TrainingResourcesConfig,
    private itemsApiService: ItemsApiService,
    private readonly marketplaceAppService: MarketplaceAppService,
    private readonly pricingService: AppPricingService,
    private readonly merchantService: MerchantService,
    private marketplacePackagesService: MarketplacePackagesApiService,
  ) {
    this.error$ = this.error$$.asObservable().pipe(filter<Error>(Boolean));
    this.partnerCurrency$ = combineLatest([this.partnerId$, this.marketId$]).pipe(
      take(1),
      switchMap(([partnerId, marketId]) =>
        this.merchantService
          .getMultiRetailConfigurations(partnerId, [marketId])
          .pipe(map((resp) => (resp.get(marketId)?.currencyCode as Currency) ?? ('USD' as Currency))),
      ),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  getProductById(productId: string, partnerId: string, marketId: string): Observable<Product> {
    const product$ = this.marketplacePackagesService
      .getMultiProducts({
        partnerId: partnerId,
        productIds: [productId],
        marketId: marketId,
      })
      .pipe(
        map((response) => {
          const product = response.products[0].product;
          return Product.fromApi(product);
        }),
        shareReplay(1),
      );

    const resellerMarketing$ = this.getAppResellerMarketing(productId);
    return combineLatest([product$, resellerMarketing$]).pipe(
      map(([product, resellerMarketing]) => {
        if (!resellerMarketing) {
          return product;
        }
        product.description = resellerMarketing.description;
        product.faqs = resellerMarketing.faqs;
        product.files = (resellerMarketing.files || []).map((fileUrl) => new File(fileUrl));
        product.keySellingPoints = resellerMarketing.keySellingPoints;
        product.lmiCategories = convertSDKLmiEnumToDisplayStrings(product.lmiCategories as unknown as LmiCategory[]);

        return product;
      }),
    );
  }

  private getAppResellerMarketing(productId): Observable<MarketingInformation> {
    const fieldMask: FieldMask = new FieldMask({
      paths: ['resellerMarketing'],
    });
    return this.marketplaceAppService.getApp(productId, fieldMask).pipe(
      map((app) => app.resellerMarketing),
      catchError((err) => {
        if (err instanceof HttpErrorResponse) {
          if (err.status === 404) {
            // we probably tried to get a custom product
            return of(undefined);
          }
          return throwError(err);
        }
      }),
      shareReplay(1),
    );
  }

  private loadActivatableItems(
    partnerId: string,
    marketId: string,
    cursor: string,
    pageSize: number,
    searchTerm: string,
  ): Observable<ListItemWithPageData> {
    return this.itemsApiService
      .listActivatableItems(
        new ListActivatableItemsRequest({
          sellerId: partnerId,
          cursor: cursor,
          pageSize: pageSize,
          sortOptions: new ListActivatableItemsSortOptions({
            sortField: ListActivatableItemsSortField.LIST_ACTIVATABLE_ITEMS_SORT_FIELD_DEFAULT_NAME,
            sortDirection: SortDirection.ASCENDING,
          }),
          filters: new ListActivatableItemsFilters({
            searchTerm: searchTerm,
            itemTypes: [ItemType.ITEM_TYPE_APP, ItemType.ITEM_TYPE_ADDON],
          }),
          storeId: marketId,
        }),
      )
      .pipe(
        this.errorStrategy$,
        map(convertListActivatableItemResponse),
        catchError((err) => {
          this.error$$.next(err);
          return throwError(err);
        }),
      );
  }

  private loadProductPrices(
    partnerId: string,
    marketId: string,
    productListItems: ListItem[],
  ): Observable<AppPrices[]> {
    const productIDs = productListItems.map((listItem) => {
      return listItem.itemId;
    });

    return this.pricingService.getMultiPricing(productIDs, new FieldMask({ paths: ['retail'] }), partnerId, marketId);
  }

  public getTableData(
    cursor: string,
    pageSize: number,
    searchTerm: string,
  ): Observable<PagedResponseInterface<ProductListPageTableRow>> {
    const listItemsWithPageData$ = combineLatest([this.partnerId$, this.marketId$]).pipe(
      switchMap(([partnerId, marketId]) =>
        this.loadActivatableItems(partnerId, marketId, cursor, pageSize, searchTerm),
      ),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    const appPricing$ = combineLatest([this.partnerId$, this.marketId$, listItemsWithPageData$]).pipe(
      switchMap(([partnerId, marketId, listItemsWithPageData]) => {
        return this.loadProductPrices(partnerId, marketId, listItemsWithPageData.items);
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    const rows$ = combineLatest([listItemsWithPageData$, appPricing$, this.partnerCurrency$]).pipe(
      map(([listItemsWithPageData, appPricing, partnerCurrency]) => {
        return convertListItemsAndPricingToTableDataRow(listItemsWithPageData, appPricing, partnerCurrency);
      }),
    );

    return combineLatest([listItemsWithPageData$, rows$]).pipe(
      map(([listItemsWithPageData, rows]) => {
        return {
          data: rows,
          pagingMetadata: {
            nextCursor: listItemsWithPageData.nextCursor,
            hasMore: listItemsWithPageData.hasMore,
            totalResults: listItemsWithPageData.totalResults,
          },
        } as PagedResponseInterface<ProductListPageTableRow>;
      }),
    );
  }
}
